/**
 * ConfirmDialog — promise-returning replacement for window.confirm().
 *
 * Mount <ConfirmHost /> once at the app root (inside <Toaster> is fine).
 * Anywhere inside, call `useConfirm()`:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Move tenant?',
 *     body: <>Move <span className="font-mono">{thing}</span> …</>,
 *     confirmLabel: 'Move',
 *     confirmVariant: 'primary',  // or 'danger'
 *   });
 *   if (!ok) return;
 *
 * Why: window.confirm() is impossible to style, doesn't match the
 * portal's modal vocabulary, blocks the JS thread, and reads as
 * "this is a 1995 webapp". The replacement uses the same chrome as
 * TenantMoveModal (fixed inset, card max-w-md, two buttons) and
 * supports rich JSX bodies (pills, monospace identifiers, etc.).
 *
 * Implementation note: the promise is held in a React ref on the
 * provider, and resolved when the user clicks Confirm or Cancel /
 * presses Escape / clicks the backdrop. Only one dialog can be open
 * at a time — calling confirm() while another is open queues behind
 * the current one (rare in practice).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface ConfirmOptions {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * 'primary' uses the accent solid button (default — for routine
   * actions). 'danger' uses a red solid button so destructive
   * actions read with appropriate weight.
   */
  confirmVariant?: 'primary' | 'danger';
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) {
    throw new Error(
      'useConfirm must be used inside <ConfirmHost> — mount it at the app root',
    );
  }
  return fn;
}

interface ActiveConfirm {
  opts: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

export function ConfirmHost({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveConfirm | null>(null);
  // Holds the pending promise's resolver across re-renders.
  const pendingRef = useRef<ActiveConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      const next: ActiveConfirm = { opts, resolve };
      pendingRef.current = next;
      setActive(next);
    });
  }, []);

  const finish = useCallback((ok: boolean) => {
    const pending = pendingRef.current;
    if (pending) {
      pending.resolve(ok);
      pendingRef.current = null;
    }
    setActive(null);
  }, []);

  // Escape dismisses (= cancel) — a common user expectation for modals.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, finish]);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {active && <ConfirmModal opts={active.opts} onFinish={finish} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmModal({
  opts,
  onFinish,
}: {
  opts: ConfirmOptions;
  onFinish: (ok: boolean) => void;
}) {
  const {
    title,
    body,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    confirmVariant = 'primary',
  } = opts;
  // Autofocus the cancel button by default — protects against an
  // accidental Enter committing a destructive action. Operators
  // who want to confirm tab once to reach the primary button.
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const confirmClass =
    confirmVariant === 'danger'
      ? 'px-3 py-1.5 bg-warn text-white rounded-md text-sm font-medium hover:bg-warn/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-warn focus-visible:ring-offset-2'
      : 'px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2';

  return (
    <div
      className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal-top"
      onClick={(e) => {
        // Backdrop click → cancel. Inner card stops propagation so
        // a click on the dialog itself doesn't dismiss it.
        if (e.target === e.currentTarget) onFinish(false);
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div className="card w-full max-w-md space-y-4 shadow-xl">
        <div>
          <h2 id="confirm-title" className="font-semibold">{title}</h2>
          <div className="text-sm text-muted mt-1.5">{body}</div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={() => onFinish(false)}
            className="px-3 py-1.5 text-sm text-muted hover:text-fg focus:outline-none focus-visible:ring-1 focus-visible:ring-border rounded-md"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => onFinish(true)}
            className={confirmClass}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
